import {Component, Expressions, matchExpression} from '@packages/systems/wfdl';
import {Err, Ok, type Result} from '@packages/utilities/fp/result';
import {concat, constant, emptyArray} from '@packages/utilities/fp/utils';

import {
  getSlotDefaultComponentTypes,
  isUserComponent,
} from '@packages/utilities/platform';

import * as DesignerErrorCodes from '@packages/systems/core/constants/ErrorCodes';

import {Qualified, QualifiedMap} from '@packages/systems/wfdl-Qualified';

export const validateNoCircularComponents = (
  stagedComponentMap: Component.ComponentMap
): Result<DesignerErrorCodes.ErrorCode, Component.ComponentMap> => {
  if (haveCycles(stagedComponentMap)) {
    return Err(DesignerErrorCodes.SymbolsCircularReferences);
  }

  return Ok(stagedComponentMap);
};

const resumeNoResult = Expressions.resume(emptyArray);

/**
 * Returns the qualified names of potential user component instances referenced
 * in the input expression.
 *
 * We want to avoid passing `components` into `getReferencedUserComponentTypes`
 * because `components` changes often which breaks memoization. We use a weak
 * approach to testing if `element` is a user component instance implemented in
 * `isUserComponent` that allows us to memoize reference search at the level of
 * individual component render expressions.
 *
 * This depends on two assumptions
 * 1. that users can only create cycles between components under the `__SitePlugin`
 *    namespace, and
 * 2. components under the `__SitePlugin` namespace can reference components
 *    from other namespaces, but that components in other namespaces cannot
 *    reference back to components from the `__SitePlugin` namespace.
 */
const getReferencedUserComponentTypes = Expressions.everything<
  Qualified<string>[]
>(concat)(
  matchExpression({
    Element: (el) =>
      isUserComponent(el.type) ? Expressions.resume([el.type]) : resumeNoResult,
    _: constant(resumeNoResult),
  })
);

type SearchSpace = QualifiedMap<
  string,
  {
    visitState: 'not visited' | 'in progress' | 'visited';
    references: Qualified<string>[];
  }
>;

export function haveCycles(components: Component.ComponentMap): boolean {
  // populate the search space with information about user component references
  const searchSpace: SearchSpace = components.map((component) => {
    const fromRender = getReferencedUserComponentTypes(
      Component.getRender(component)
    );
    const fromSlotDefaults = getSlotDefaultComponentTypes(component);
    return {
      visitState: 'not visited',
      references: concat(fromRender)(fromSlotDefaults),
    };
  });

  return (
    searchSpace.find((_entry, componentName) =>
      hasCycles(componentName, searchSpace)
    ) !== undefined
  );
}

/**
 * Determines if a walk starting from a component `name` in a search `space`
 * has any cycles.
 *
 * Please note! This method mutates entries in the search space, updating
 * `visitState` properties to indicate entries that are being or have been
 * visited.
 */
function hasCycles(name: Qualified<string>, space: SearchSpace): boolean {
  const entry = name ? space.get(name) : null;
  if (
    !entry ||
    // if the entry has been successfully visited, we know there are no cycles
    entry.visitState === 'visited'
  ) {
    return false;
  }

  if (entry.visitState === 'in progress') {
    // Cycle found! We walked into an entry with a visit in progress.
    return true;
  }

  // indicate that the visit to this entry is in progress
  entry.visitState = 'in progress';
  for (const childName of entry.references) {
    // paint the search space recursively
    if (hasCycles(childName, space)) {
      return true;
    }
  }
  // indicate that this entry has been successfully visited and has no cycles
  entry.visitState = 'visited';

  return false;
}
